import { useEffect, useState } from 'react';
import { GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import { asyncGetSamplesPqr } from '../../../../../services/pqr.service';
import { ISelect } from '../../../../../shared/types/shared';
import { Pqr } from '../../../../../shared/types/modules/pqr';
import { TableTooltip } from '../../../../components/general/table';
import { DateTooltip } from '../../../../components/general/dateTooltip';
import { useNavigate } from 'react-router-dom';
import { SamplesFilter } from '../../../../../shared/types/legalProceedings/samples';
import { SamplesListActions } from './actions';
import { isTrainerOrQuality } from '../../../../../shared/types/profiles';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../../redux/store';

export const useSamplesList = (filters?: SamplesFilter) => {
    const navigate = useNavigate();
    const { logUser } = useSelector((state: RootState) => state.general);
    const { type } = logUser || {};
    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState<Pqr[]>([]);
    const [page, setPage] = useState<number>(0);
    const [pageSize, setPageSize] = useState<number>(10);
    const [total, setTotal] = useState<number>(0);
    const [finalFilters, setFinalFilters] = useState<SamplesFilter>({});

    const paginator = (model: GridPaginationModel) => {
        if (model.page !== page) {
            setPage(model.page);
        } else if (model.pageSize !== pageSize) {
            setPageSize(model.pageSize);
        }
    };

    const columns: GridColDef<Pqr>[] = [];

    if (!isTrainerOrQuality(type)) {
        columns.push({
            field: 'actions',
            headerName: 'Acciones',
            width: 100,
            sortable: false,
            disableColumnMenu: true,
            filterable: false,
            align: 'center',
            headerAlign: 'center',
            renderCell: (row) => <SamplesListActions id={row.id} />,
        });
    }

    columns.push(
        {
            field: 'caseId',
            headerName: 'ID Caso',
            minWidth: 120,
            flex: 0.4,
            renderCell: (row) => (
                <TableTooltip value={row.row.callInfo?.callId || ''} />
            ),
        },
        {
            field: 'consecutiveFormat',
            headerName: 'PQR ID',
            minWidth: 120,
            flex: 0.4,
        },
        {
            field: 'closingDate',
            headerName: 'Fecha de cierre',
            minWidth: 130,
            flex: 0.4,
            renderCell: (row) =>
                row.row?.statusManagement?.closingDate && (
                    <DateTooltip
                        date={row.row?.statusManagement?.closingDate}
                        format="DD-MM-YYYY"
                    />
                ),
        },
        {
            field: 'audited',
            headerName: 'Auditado',
            minWidth: 120,
            flex: 0.4,
        },
        {
            field: 'state',
            headerName: 'Estado',
            minWidth: 150,
            flex: 0.6,
            renderCell: (row) => (
                <TableTooltip
                    value={(row.row.info?.status as ISelect)?.name || ''}
                />
            ),
        },
        {
            field: 'furagDate',
            headerName: 'Fecha Furag',
            minWidth: 130,
            flex: 0.4,
            renderCell: (row) =>
                row.row.statusManagement?.furagDate && (
                    <DateTooltip
                        date={row.row.statusManagement?.furagDate}
                        format="DD-MM-YYYY"
                    />
                ),
        },
        {
            field: 'area1',
            headerName: 'Área encargada',
            minWidth: 150,
            flex: 0.6,
            renderCell: (row) => (
                <TableTooltip
                    value={(row.row?.redirect?.area1 as ISelect)?.name || ''}
                />
            ),
        },
        {
            field: 'type',
            headerName: 'Tipo PQR',
            minWidth: 150,
            flex: 0.6,
            renderCell: (row) => (
                <TableTooltip
                    value={(row.row?.data?.type as ISelect)?.name || ''}
                />
            ),
        },
        {
            field: 'document',
            headerName: 'Identificación contacto',
            minWidth: 120,
            flex: 0.4,
            renderCell: (row) => (
                <TableTooltip value={row.row.account?.document || ''} />
            ),
        },
        {
            field: 'name',
            headerName: 'Nombre contacto',
            minWidth: 150,
            flex: 0.6,
            renderCell: (row) => (
                <TableTooltip
                    value={`${row.row.account?.name} ${row.row.account?.lastName}`}
                />
            ),
        },
        {
            field: 'half',
            headerName: 'Medio',
            minWidth: 150,
            flex: 0.6,
            renderCell: (row) => (
                <TableTooltip
                    value={(row.row.data?.half as ISelect)?.name || ''}
                />
            ),
        },
        {
            field: 'created_at',
            headerName: 'Fecha de creación',
            minWidth: 120,
            flex: 0.6,
            renderCell: (row) => <DateTooltip date={row.row.created_at} />,
        },
        {
            field: 'audit',
            headerName: 'Auditado',
            minWidth: 120,
            flex: 0.4,
            align: 'center',
            renderCell: (row) => <>{row.row.closing?.audit ? 'Si' : 'No'}</>,
        }
    );

    useEffect(() => {
        setPage(0);
        setFinalFilters(filters || {});
    }, [filters]);

    useEffect(() => {
        setLoading(true);
        asyncGetSamplesPqr(finalFilters, page, pageSize)
            .then((res) => {
                setRows(res.items);
                setTotal(res.length);
            })
            .finally(() => setLoading(false));
    }, [page, pageSize, finalFilters]);

    return {
        navigate,
        columns,
        rows,
        page,
        pageSize,
        paginator,
        loading,
        total,
    };
};
